import api from '../api';
import * as Keys from '../constants/systemSettingKeys';
import { REFRESH_LATER_EXPIRES_AT } from '../constants/storageKeys';

export class AppVersion {
    announcement = null;
    hasCheckedAfterAuthentication = false;
    listeners = new Set();

    subscribe = listener => {
        this.listeners.add(listener);
        listener();
        return () => this.listeners.delete(listener);
    };

    notify = () => this.listeners.forEach(listener => listener());

    // We want to verify the js version immediately after logging in (called from AuthStore)
    checkAfterAuthentication = async () => {
        this.hasCheckedAfterAuthentication = true;
        try {
            const { data } = await api.SystemSettings.all();
            const systemSettings = Array.isArray(data) ? data : [];
            const value = key => {
                const entry = systemSettings.find(s => s.key === key);
                return entry ? entry.value : null;
            };
            const source = value(Keys.DEPLOYED_JAVASCRIPT);

            if (source) {
                this.handleAnnouncement({
                    payload: {
                        source,
                        deploymentTimestamp: value(Keys.DEPLOYED_JAVASCRIPT_TIMESTAMP)
                    }
                });
            }
        } catch (error) {
            // Version lookup failure must not turn a successful authentication into a logout.
            console.warn('Unable to check deployed application version', error);
        }
    };

    handleAnnouncement = notification => {
        if (!notification || !notification.payload) { return; }
        const { source, deploymentTimestamp } = notification.payload;

        const timestamp = String(deploymentTimestamp || '');
        if (timestamp && !/^\d{17}$/.test(timestamp)) { return; }
        if (this.announcement && this.announcement.deploymentTimestamp &&
            (!timestamp || timestamp < this.announcement.deploymentTimestamp)) { return; }

        this.announcement = { source, deploymentTimestamp: timestamp };
        this.notify();
    };

    isLatest = () => {
        if (!this.announcement) { 
            return true; 
        }
        // Example source: https://example.com/static/js/main.4d25b26b.chunk.js?v=2#loaded
        // split(/[?#]/)[0] keeps everything before the first ? or #, so you get https://example.com/static/js/main.4d25b26b.chunk.js
        // and then split with '/'
        const filename = source     => source.split(/[?#]/)[0].split('/').pop();
        const deployedFilename = filename(this.announcement.source);
        var latest = Array.from(document.scripts).some(script => {
            return filename(script.src) === deployedFilename;
        });
        return latest;
    };

    shouldPrompt = () => {
        if (this.isLatest()) { return false; }
        const expiresAt = Number(window.localStorage.getItem(REFRESH_LATER_EXPIRES_AT));
        return !expiresAt || Date.now() >= expiresAt;   // note that Date.new() returns milliseconds (number of milliseconds since January 1, 1970 UTC), not a Date object
    };

    defer = () => {
        // defer 15 minutes
        window.localStorage.setItem(REFRESH_LATER_EXPIRES_AT, Date.now() + 15 * 60 * 1000);
    };

    clear = () => {
        this.hasCheckedAfterAuthentication = false;
        this.announcement = null;
    };
}

export default new AppVersion();
